export interface SEOData {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  structuredData?: any;
}

export const updateSEO = (data: SEOData) => {
  // Update title
  document.title = data.title;
  
  // Update meta description
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', data.description);
  }
  
  // Update meta keywords
  const metaKeywords = document.querySelector('meta[name="keywords"]');
  if (metaKeywords) {
    metaKeywords.setAttribute('content', data.keywords);
  }
  
  // Update canonical URL
  if (data.canonical) {
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', data.canonical);
  }
  
  // Update Open Graph tags
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle && data.ogTitle) {
    ogTitle.setAttribute('content', data.ogTitle);
  }
  
  const ogDescription = document.querySelector('meta[property="og:description"]');
  if (ogDescription && data.ogDescription) {
    ogDescription.setAttribute('content', data.ogDescription);
  }
  
  const ogImage = document.querySelector('meta[property="og:image"]');
  if (ogImage && data.ogImage) {
    ogImage.setAttribute('content', data.ogImage);
  }
  
  // Update structured data
  if (data.structuredData) {
    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }
    
    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data.structuredData);
    document.head.appendChild(script);
  }
};

export const getBaseURL = () => {
  return import.meta.env.VITE_SITE_URL || 'https://dluxmen-catalog.com';
};

// SEO data for each page
export const seoData = {
  home: {
    title: 'DLUXMEN - Moda Masculina Premium São Paulo Brás | Camisas Luxo Atacado Varejo',
    description: 'DLUXMEN - Loja premium de moda masculina no Brás, São Paulo. Camisas de luxo, acessórios e roupas masculinas de alta qualidade. Atacado e varejo com as melhores marcas. Experience Collection 2025.',
    keywords: 'DLUXMEN, moda masculina, camisas luxo, Brás São Paulo, atacado roupas masculinas, varejo premium, acessórios masculinos',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "ClothingStore",
      "name": "DLUXMEN",
      "description": "Loja premium de moda masculina no Brás, São Paulo. Camisas de luxo, acessórios e roupas masculinas de alta qualidade.",
      "url": `${getBaseURL()}`,
      "telephone": "+5511945589527",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Rua Monsenhor Andrade, 987 - Brás, Corredor Principal Box L 1136, 1º Andar",
        "addressLocality": "São Paulo",
        "addressRegion": "SP",
        "postalCode": "03009-100",
        "addressCountry": "BR"
      },
      "openingHours": "Mo-Sa 08:00-15:50",
      "sameAs": [
        "https://www.instagram.com/dlux_men_?igsh=dGRyczc3ZWYzcTlw&utm_source=qr"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Catálogo DLUXMEN",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "Camisas Masculinas Premium",
              "category": "Moda Masculina"
            }
          }
        ]
      }
    }
  },
  masculino: {
    title: 'Roupas Masculinas Premium DLUXMEN | Camisas Calças Blazers São Paulo',
    description: 'Coleção masculina DLUXMEN com camisas premium, calças sociais, blazers e roupas masculinas de luxo. Moda masculina de alta qualidade no Brás, São Paulo. Atacado e varejo.',
    keywords: 'roupas masculinas, camisas premium, blazers masculinos, calças sociais, moda masculina luxo, DLUXMEN masculino',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Coleção Masculina DLUXMEN",
      "description": "Coleção completa de roupas masculinas premium incluindo camisas, blazers, calças e acessórios de luxo.",
      "url": `${getBaseURL()}/masculino`,
      "mainEntity": {
        "@type": "ItemList",
        "name": "Produtos Masculinos",
        "numberOfItems": 50
      }
    }
  },
  feminino: {
    title: 'Roupas Femininas Premium DLUXMEN | Blusas Vestidos Acessórios São Paulo',
    description: 'Coleção feminina DLUXMEN com blusas elegantes, vestidos sofisticados e acessórios femininos de luxo. Moda feminina premium no Brás, São Paulo. Qualidade e estilo.',
    keywords: 'roupas femininas, blusas elegantes, vestidos luxo, acessórios femininos, moda feminina premium, DLUXMEN feminino',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Coleção Feminina DLUXMEN",
      "description": "Coleção sofisticada de roupas femininas incluindo blusas, vestidos e acessórios de luxo.",
      "url": `${getBaseURL()}/feminino`
    }
  },
  bags: {
    title: 'Bolsas Premium DLUXMEN | Bolsas Masculinas Femininas Luxo São Paulo',
    description: 'Coleção de bolsas premium DLUXMEN. Bolsas masculinas e femininas de luxo, mochilas executivas e acessórios de couro. Qualidade superior no Brás, São Paulo.',
    keywords: 'bolsas premium, bolsas masculinas, bolsas femininas, mochilas executivas, acessórios couro, DLUXMEN bags',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Coleção de Bolsas DLUXMEN",
      "description": "Coleção premium de bolsas masculinas e femininas, mochilas executivas e acessórios de couro de alta qualidade.",
      "url": `${getBaseURL()}/bags`
    }
  },
  accessories: {
    title: 'Acessórios Premium DLUXMEN | Cintos Carteiras Gravatas Luxo São Paulo',
    description: 'Acessórios masculinos e femininos premium DLUXMEN. Cintos de couro, carteiras, gravatas, abotoaduras e acessórios de luxo. Qualidade superior no Brás, São Paulo.',
    keywords: 'acessórios premium, cintos couro, carteiras luxo, gravatas, abotoaduras, acessórios masculinos, DLUXMEN acessórios',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Coleção de Acessórios DLUXMEN",
      "description": "Acessórios premium incluindo cintos de couro, carteiras, gravatas e abotoaduras de alta qualidade.",
      "url": `${getBaseURL()}/accessories`
    }
  },
  watches: {
    title: 'Relógios Premium DLUXMEN | Relógios Masculinos Femininos Luxo São Paulo',
    description: 'Coleção de relógios premium DLUXMEN. Relógios masculinos e femininos de luxo, smartwatches e relógios clássicos. Qualidade e elegância no Brás, São Paulo.',
    keywords: 'relógios premium, relógios masculinos, relógios femininos, smartwatches, relógios luxo, DLUXMEN watches',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Coleção de Relógios DLUXMEN",
      "description": "Coleção premium de relógios masculinos e femininos, smartwatches e relógios clássicos de luxo.",
      "url": `${getBaseURL()}/watches`
    }
  },
  italian: {
    title: 'Coleção Italiana DLUXMEN | Roupas Italianas Premium São Paulo Brás',
    description: 'Coleção italiana DLUXMEN com roupas e acessórios importados da Itália. Estilo italiano autêntico, qualidade premium e elegância europeia no Brás, São Paulo.',
    keywords: 'coleção italiana, roupas italianas, estilo italiano, moda europeia, importados Itália, DLUXMEN italian',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Coleção Italiana DLUXMEN",
      "description": "Coleção exclusiva de roupas e acessórios importados da Itália com estilo autêntico e qualidade premium.",
      "url": `${getBaseURL()}/italian`
    }
  },
  line11: {
    title: 'Linha 1.1 DLUXMEN | Coleção Premium Exclusiva São Paulo',
    description: 'Linha 1.1 DLUXMEN - Coleção premium exclusiva com peças selecionadas de alta qualidade. Estilo sofisticado e elegância única no Brás, São Paulo.',
    keywords: 'linha 1.1, coleção exclusiva, peças premium, estilo sofisticado, DLUXMEN line 1.1',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Linha 1.1 DLUXMEN",
      "description": "Coleção premium exclusiva Linha 1.1 com peças selecionadas de alta qualidade e estilo sofisticado.",
      "url": `${getBaseURL()}/line11`
    }
  },
  line12: {
    title: 'Linha 1.2 DLUXMEN | Coleção Especial Premium São Paulo',
    description: 'Linha 1.2 DLUXMEN - Coleção especial premium com design inovador e qualidade superior. Peças exclusivas e estilo contemporâneo no Brás, São Paulo.',
    keywords: 'linha 1.2, coleção especial, design inovador, peças exclusivas, estilo contemporâneo, DLUXMEN line 1.2',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Linha 1.2 DLUXMEN",
      "description": "Coleção especial premium Linha 1.2 com design inovador e peças exclusivas de estilo contemporâneo.",
      "url": `${getBaseURL()}/line12`
    }
  },
  outlet: {
    title: 'Outlet DLUXMEN | Roupas Premium com Desconto São Paulo Brás',
    description: 'Outlet DLUXMEN com roupas premium com desconto. Camisas, acessórios e peças de luxo com preços especiais. Oportunidades únicas no Brás, São Paulo.',
    keywords: 'outlet DLUXMEN, roupas desconto, premium outlet, ofertas especiais, preços promocionais, liquidação luxo',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Outlet DLUXMEN",
      "description": "Seção outlet com roupas premium e acessórios de luxo com preços especiais e descontos exclusivos.",
      "url": `${getBaseURL()}/outlet`
    }
  }
};